import { TextareaHTMLAttributes } from 'react'
import { Controller, useWatch } from 'react-hook-form'

import { TCommonFields } from '../../types'
import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'
import { TControlledProps, TInputField } from './input-field'

export type TTextareaField<T> = TCommonFields<T> & {
    type: 'textarea'
    labelTransition?: boolean
    default?: string
    loadDefault?: () => Promise<string>
    props?: TControlledProps<TextareaHTMLAttributes<HTMLTextAreaElement>>
}

type TProps<T> = {
    rowField: TTextareaField<T> | TInputField<T>
    fieldName: string
    labelWidth?: string
}

const TextareaField = <T,>({ rowField, fieldName, labelWidth }: TProps<T>) => {
    'use no memo'
    const { disabled, required } = useFieldState(rowField)
    const value = useWatch({ name: fieldName })

    return (
        <FieldRow
            fieldName={fieldName}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className="form-input"
            labelTransition={rowField.labelTransition}
            filled={rowField.labelTransition && !['', undefined, null].includes(value)}
        >
            <Controller
                name={fieldName}
                rules={getFieldRules(rowField, required)}
                render={({ field }) => (
                    <textarea
                        {...field}
                        {...(rowField.props as TTextareaField<T>['props'])}
                        id={fieldName}
                        value={field.value ?? ''}
                        required={required}
                        disabled={disabled}
                        onChange={e => {
                            field.onChange(e)
                            rowField.onChange?.(e.target.value)
                        }}
                    />
                )}
            />
        </FieldRow>
    )
}

export default TextareaField
